import React, { useState } from 'react';
import { useApp } from '../context/AppContext';

/**
 * MenuItemCard — a single menu item displayed in the items list.
 * Shows image, name, price, popular badge, and quick "add" button.
 */
export default function MenuItemCard({ item, restaurant, onClick, index }) {
  const { loc, addToCart, cartNotification, t } = useApp();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isNotifying = cartNotification === item.id;
  const symbol = restaurant?.currencySymbol || 'DH';

  const handleAdd = (e) => {
    e.stopPropagation();
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
    });
  };

  return (
    <div
      style={{
        ...styles.card,
        animationDelay: `${index * 0.05}s`,
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {/* Image section */}
      <div style={styles.imageWrap}>
        {!imageLoaded && !imageError && (
          <div className="skeleton" style={styles.imageSkeleton} />
        )}
        {!imageError ? (
          <img
            src={item.image}
            alt={loc(item.name)}
            style={{
              ...styles.image,
              opacity: imageLoaded ? 1 : 0,
            }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div style={styles.imagePlaceholder}>🍽️</div>
        )}
        {item.popular && (
          <span style={styles.popularBadge}>⭐ {t('popular')}</span>
        )}
      </div>

      {/* Info section */}
      <div style={styles.info}>
        <div style={styles.infoTop}>
          <h3 style={styles.name}>{loc(item.name)}</h3>
          <p style={styles.description}>{loc(item.description)}</p>
        </div>
        <div style={styles.infoBottom}>
          <span style={styles.price}>
            {item.price} <span style={styles.currency}>{symbol}</span>
          </span>
          <button
            style={{
              ...styles.addBtn,
              ...(isNotifying ? styles.addBtnSuccess : {}),
            }}
            onClick={handleAdd}
            aria-label={`Add ${loc(item.name)} to cart`}
          >
            {isNotifying ? '✓' : '+'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    display: 'flex',
    gap: '0.85rem',
    padding: '0.85rem',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-light)',
    cursor: 'pointer',
    transition: 'all var(--transition)',
    animation: 'fadeInUp 0.4s ease both',
    position: 'relative',
    overflow: 'hidden',
  },
  imageWrap: {
    flexShrink: 0,
    width: '90px',
    height: '90px',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: 'var(--bg-tertiary)',
  },
  imageSkeleton: {
    position: 'absolute',
    inset: 0,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'opacity 0.3s ease',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    backgroundColor: 'var(--bg-tertiary)',
  },
  popularBadge: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    padding: '2px 6px',
    fontSize: '0.6rem',
    fontWeight: 600,
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    borderRadius: 'var(--radius-full)',
    letterSpacing: '0.03em',
  },
  info: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: 0,
  },
  infoTop: {
    flex: 1,
  },
  name: {
    fontFamily: 'var(--font-display)',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.2rem',
    lineHeight: 1.3,
  },
  description: {
    fontSize: '0.78rem',
    color: 'var(--text-muted)',
    lineHeight: 1.4,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  infoBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '0.5rem',
  },
  price: {
    fontWeight: 700,
    fontSize: '1.05rem',
    color: 'var(--accent)',
  },
  currency: {
    fontSize: '0.75rem',
    fontWeight: 500,
    opacity: 0.8,
  },
  addBtn: {
    width: '34px',
    height: '34px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    fontWeight: 600,
    transition: 'all var(--transition-fast)',
    boxShadow: '0 2px 8px var(--accent-glow)',
  },
  addBtnSuccess: {
    backgroundColor: 'var(--success)',
    transform: 'scale(1.1)',
  },
};
